import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { JimProjectConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type JimFormValues = JimProjectConfigurationDto;

export function JimPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();
  const canManageJim = hasPermission((x) => x.integrations.canManageJim);
  const { data, isFetching, error } = useQuery({
    ...integrationQueries.jimConfiguration(),
    enabled: canManageJim,
  });
  const editMutation = integrationMutations.useEditJimConfiguration();
  const testMutation = integrationMutations.useTestJimConnection();

  if (!canManageJim) {
    return <ErrorPage status={404} />;
  }
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.jim.title");
  const connectionResult = testMutation.data;

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <JimForm
        defaultValues={{
          enabled: data?.enabled ?? false,
          username: data?.username ?? "",
          password: "",
          baseUrl: data?.baseUrl ?? "",
          hasPassword: data?.hasPassword ?? false,
        }}
        isSubmitting={editMutation.isPending}
        isTesting={testMutation.isPending}
        connectionResult={connectionResult}
        onSubmit={(values) => editMutation.mutate({ ...values, password: values.password || undefined })}
        onTest={() => testMutation.mutate()}
      />
    </DocumentPage>
  );
}

function JimForm({
  defaultValues,
  isSubmitting,
  isTesting,
  connectionResult,
  onSubmit,
  onTest,
}: {
  defaultValues: JimFormValues;
  isSubmitting: boolean;
  isTesting: boolean;
  connectionResult?: { success: boolean; message?: string };
  onSubmit: (data: JimFormValues) => void;
  onTest: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<JimFormValues>({ defaultValues });
  const enabled = useWatch({ control: form.control, name: "enabled" });

  return (
    <Form.Root formMethods={form} onSubmit={onSubmit}>
      <Form.Container title={t("page.integration.jim.title")}>
        <Form.Body>
          <Stack gap="md">
            <FormCheckbox<JimFormValues, "enabled">
              id="enabled"
              name="enabled"
              label={t("common.action.enable")}
              onChange={(event) => {
                if (!event.target.checked) {
                  form.clearErrors();
                }
              }}
            />

            <FormField label={t("page.integration.jim.form.username")} required>
              <FormInput<JimFormValues, "username">
                id="username"
                name="username"
                disabled={!enabled}
                rules={{
                  validate: {
                    required: enabled
                      ? createRequiredStringRule(t, "page.integration.jim.form.username")
                      : () => undefined,
                  },
                }}
              />
            </FormField>

            <FormField
              label={t("page.integration.jim.form.password")}
              description={defaultValues.hasPassword ? t("page.integration.jim.form.password-preserved") : undefined}
              required={enabled && !defaultValues.hasPassword}
            >
              <FormInput<JimFormValues, "password">
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                disabled={!enabled}
                placeholder={
                  defaultValues.hasPassword ? t("page.integration.jim.form.password-placeholder") : undefined
                }
                rules={{
                  validate: {
                    required:
                      enabled && !defaultValues.hasPassword
                        ? createRequiredStringRule(t, "page.integration.jim.form.password")
                        : () => undefined,
                  },
                }}
              />
            </FormField>

            <FormField label={t("page.integration.jim.form.base-url")}>
              <FormInput<JimFormValues, "baseUrl">
                id="baseUrl"
                name="baseUrl"
                type="url"
                disabled={!enabled}
                placeholder="https://jim-utrecht.nl"
              />
            </FormField>

            {connectionResult && (
              <Notice
                data-testid="jim-connection-result"
                variant={connectionResult.success ? "success" : "danger"}
                message={
                  connectionResult.message ??
                  t(
                    connectionResult.success
                      ? "page.integration.jim.connection.success"
                      : "page.integration.jim.connection.failed",
                  )
                }
              />
            )}
          </Stack>
        </Form.Body>

        <Form.Footer
          renderSecondaryAction={() => (
            <Button
              type="button"
              variant="soft"
              disabled={!enabled || !defaultValues.hasPassword || form.formState.isDirty}
              isLoading={isTesting}
              onClick={onTest}
            >
              {t("page.integration.jim.connection.test")}
            </Button>
          )}
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
